

import { OidcClient } from '@axa-fr/oidc-client';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Prod_Custom } from '../Domains_Obj/Prod_Custom';
import { PreProd_Custom } from '../Domains_Obj/PreProd_Custom';
import { QA_Custom } from '../Domains_Obj/QA_Custom';
import { SESTest_Custom } from '../Domains_Obj/SesTest_Custom';

export const useIdentityServer = process.env.REACT_APP_USE_IDENTITY_SERVER ?
    process.env.REACT_APP_USE_IDENTITY_SERVER.toLowerCase() === 'true' : false;

const foundIdentityRedirectUrlElement = document.getElementById('IdentityRedirectUrl');
const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
const authority = process.env.REACT_APP_OPEN_ID_AUTHORITY;

const getPortalBaseUrl = () => {
    let redirectUrl = foundIdentityRedirectUrlElement && foundIdentityRedirectUrlElement.value ?
        foundIdentityRedirectUrlElement.value : baseUrl;

    if (!window.location.href.includes(authority) &&
        redirectUrl.indexOf('http://') !== 0 && redirectUrl.indexOf('https://') !== 0) {

        redirectUrl = window.location.protocol + "//" + window.location.host;
        if (process.env.REACT_APP_API_URL) {
            redirectUrl += process.env.REACT_APP_API_URL
        }

        if (redirectUrl[redirectUrl.length - 1] !== "/") {
            redirectUrl += "/";
        }
    }
    return redirectUrl;
}

const getIdentityUrl = () => {
    let authorityAbsoluteUrl = baseUrl;
    if (window.location.href.includes(authority)) {
        // If we set the authority absolute URL, use that cause it's likely an override
        if (authority.indexOf('http://') === 0 || authority.indexOf('https://') === 0) {
            authorityAbsoluteUrl = authority;
        }
        else {
            authorityAbsoluteUrl = window.location.protocol + "//" + window.location.host + "/" + authority;
        }
    }
    else {
        if (authority.indexOf('http://') === 0 || authority.indexOf('https://') === 0) {
            authorityAbsoluteUrl = authority;
        }

        if (authorityAbsoluteUrl.indexOf('http://') !== 0 && authorityAbsoluteUrl.indexOf('https://') !== 0) {
            authorityAbsoluteUrl = window.location.protocol + "//" + window.location.hostname + "/" + authority;
        }
    }
    return authorityAbsoluteUrl;
};

export const getIdentityServerConfiguration = () => {
    let redirectUrl = getPortalBaseUrl();
    let authorityAbsoluteUrl = getIdentityUrl();

    const oidcConfig = {
        authority: authorityAbsoluteUrl,
        client_id: process.env.REACT_APP_OPEN_ID_CLIENT_ID,
        client_secret: process.env.REACT_APP_OPEN_ID_CLIENT_SECRET,
        redirect_uri: redirectUrl + 'authentication/callback',
        silent_redirect_uri: redirectUrl + 'authentication/silent-callback',
        scope: 'openid profile roles address ses',
        preload_user_info: true,
        token_renew_mode: 'access_token_invalid'
    };

    // Unless the customizations gets more complex and includes a lot of localization this is a quick
    // way to load the customizations without having to call a backend API. If a duplicate domain exists
    // between the environments, we'll still grab the first one which would prioritize higher environments
    let customizations = [...Prod_Custom, ...PreProd_Custom, ...QA_Custom, ...SESTest_Custom];
    const foundCustomization = customizations
        .find(x => x.domainName?.toLowerCase() === window.location.host.toLowerCase());

    if (foundCustomization?.data) {
        // Since we're loading the customizations, we'll go ahead and just set the custom page title here. 
        // This doesn't really make sense to be here but we'll do it for now until the customizations get more complex
        if (foundCustomization.data.layout_title) {
            document.title = foundCustomization.data.layout_title;
        }

        if (foundCustomization.data.acr_values) {
            oidcConfig.extras = { 'acr_values': foundCustomization.data.acr_values };
        }
    }

    return oidcConfig;
};

export const hasRole = (token, roleName) => {
    if ((Array.isArray(token.role) && token.role.find((e) => e.toUpperCase() === roleName)) ||
        (!Array.isArray(token.role) && token.role?.toUpperCase() === roleName)) {
        return true;
    }
    return false;
};

export const getUserRole = (token) => {
    let role = "USER";

    if (hasRole(token, "ADMINISTRATOR")) {
        role = "ADMINISTRATOR";
    }

    if (hasRole(token, "SES ADMINISTRATOR")) {
        role = "SES ADMINISTRATOR";
    }

    return role;
};

export const getUserSession = (token) => {
    let notifyFlag = false;
    let createUserFlag = false;


    if (hasRole(token, "ENSACCESS")) {
        notifyFlag = true;
    }

    if (hasRole(token, "CREATEUSERS")) {
        createUserFlag = true;
    }

    return {
        ...token,
        HasNotify: notifyFlag,
        CreateUserFlag: createUserFlag,
    };
};

export const getDefaultLandingPage = (token, userEmails) => {
    let isAdmin = hasRole(token, "ADMINISTRATOR") || hasRole(token, "SES ADMINISTRATOR");

    switch (token?.registration_status) {
        case "PreRegistrationStarted":
            return "/EULA";
        case "InvitationStarted":
            return "/EULA";
        case "PasswordRecovery":
            return "/password-change-preRegistration";
        case "NPIComplete":
            return "/pre-registration";
        case "ContactDetailsComplete":
            return "/experian-sso";
        case "ExperionIDPFailed":
            return "/experian-results";
        case "Migration":
            var npi = token?.IDs.Where((x) => x.IDType == 0).FirstOrDefault();
            if (npi == null) {
                return "/dashboard";
            }
            return "/no-direct-email";
        default:
            if (isAdmin) {
                return "/dashboard";
            } else {
                if (Array.isArray(userEmails) && userEmails.length > 0) {
                    return "/webmail";
                } else {
                    return "/no-direct-email";
                }
            }
    }
};

const defaultConfigurationName = 'default';

const defaultIsAuthenticated = (getOidc, configurationName) => {
    let isAuthenticated = false;

    if (useIdentityServer) {
        const oidc = getOidc(configurationName);
        if (oidc) {
            isAuthenticated = getOidc(configurationName).tokens != null;
        }
    }
    return isAuthenticated;
};

export const usePortalAuthentication = (configurationName = defaultConfigurationName) => {
    const getOidc = OidcClient.get;
    const [isAuthenticated, setIsAuthenticated] = useState(defaultIsAuthenticated(getOidc, configurationName));
    const navigate = useNavigate();

    useEffect(() => {
        if (useIdentityServer) {
            let isMounted = true;
            const oidc = getOidc(configurationName);
            setIsAuthenticated(defaultIsAuthenticated(getOidc, configurationName));
            const newSubscriptionId = oidc.subscribeEvents((name, data) => {
                if (name === OidcClient.eventNames.logout_from_another_tab || name === OidcClient.eventNames.logout_from_same_tab || name === OidcClient.eventNames.token_aquired) {
                    if (isMounted) {
                        setIsAuthenticated(defaultIsAuthenticated(getOidc, configurationName));
                    }
                }
            });
            return () => {
                isMounted = false;
                oidc.removeEventSubscription(newSubscriptionId);
            };
        }
    }, [configurationName]);

    const login = (callbackPath = undefined, extras = null, silentLoginOnly = false) => {
        if (useIdentityServer) {
            return getOidc(configurationName).loginAsync(getPortalBaseUrl(), extras, false, undefined, silentLoginOnly);
        } else {
            return navigate("/");
        }
    };

    const logout = (callbackPath = undefined, extras = null) => {
        if (useIdentityServer) {
            return getOidc(configurationName).logoutAsync(getPortalBaseUrl(), extras);
        } else {
            return navigate("/signout");
        }
    };

    return {
        login, logout, isAuthenticated
    };
};